import React, { useState } from 'react';
import { UI, FONT} from "../../constants/tokens";
import { toHebrew, PasswordField } from './AuthPage';
import logo256 from '../../assets/suricata-logo-256.png';

// Where a recovery link lands. By the time this renders the link has already
// been traded for a real session, so the user is technically signed in — which
// is exactly why this screen has to exist: without it the app would open on the
// home screen and never ask for the new password the mail promised.
//
// Two fields rather than one. A password box hides what it holds, and a typo in
// a password the user is setting for the first time locks them out of the
// account they just recovered; the confirmation is checked here, before the
// request, so that mistake costs a moment instead of another mail.
export default function ResetPasswordPage({ onUpdatePassword, onDone }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [error, setError]       = useState(null);
  const [busy, setBusy]         = useState(false);
  const [done, setDone]         = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (password !== confirm) {
      setError('הסיסמאות אינן זהות');
      return;
    }
    if (password.length < 6) {
      setError('הסיסמה חייבת להכיל לפחות 6 תווים');
      return;
    }

    setBusy(true);
    const err = await onUpdatePassword(password);
    setBusy(false);
    if (err) {
      setError(toHebrew(err.message));
      return;
    }
    setDone(true);
  };

  return (
    <div dir="rtl" style={{
      minHeight: '100dvh',
      background: UI.surface.page,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: FONT.body,
      padding: '24px 16px',
    }}>
      <div style={{ width: '100%', maxWidth: 380 }}>

        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <img src={logo256} alt="Suricata" style={{
            width: 52, height: 52, borderRadius: '22%',
            background: UI.logoPlate, objectFit: 'cover', marginBottom: 12,
          }}/>
          <div dir="ltr" style={{ fontWeight: 700, fontSize: 22, color: UI.text.primary, letterSpacing: '-0.5px', fontFamily: FONT.latin, unicodeBidi: 'isolate' }}>
            Suricata
          </div>
        </div>

        <div style={{
          background: UI.surface.raised,
          border: `1px solid ${UI.line.hairline}`,
          borderRadius: 6,
          padding: '28px 24px',
        }}>
          {done ? (
            <>
              <div style={{ fontSize: 17, fontWeight: 700, color: UI.text.primary, marginBottom: 8 }}>
                הסיסמה עודכנה
              </div>
              <div style={{ fontSize: 13, color: UI.text.secondary, lineHeight: 1.7, marginBottom: 20 }}>
                אתה מחובר לחשבון · בכניסה הבאה השתמש בסיסמה החדשה
              </div>
              <button type="button" onClick={onDone} style={{
                width: '100%', padding: '12px 0',
                background: UI.accent.base, border: 'none', borderRadius: 6,
                cursor: 'pointer', color: UI.surface.page,
                fontWeight: 800, fontSize: 16,
                fontFamily: FONT.body,
              }}>
                המשך לאפליקציה
              </button>
            </>
          ) : (
            <>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 17, fontWeight: 700, color: UI.text.primary, marginBottom: 6 }}>
                  בחירת סיסמה חדשה
                </div>
                <div style={{ fontSize: 13, color: UI.text.secondary, lineHeight: 1.6 }}>
                  הזן סיסמה חדשה לחשבון · לפחות 6 תווים
                </div>
              </div>

              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, color: UI.text.secondary, fontWeight: 600, marginBottom: 6 }}>
                    סיסמה חדשה
                  </label>
                  <PasswordField
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="לפחות 6 תווים"
                    autoComplete="new-password"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 13, color: UI.text.secondary, fontWeight: 600, marginBottom: 6 }}>
                    אימות סיסמה
                  </label>
                  <PasswordField
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    placeholder="הזן שוב"
                    autoComplete="new-password"
                  />
                </div>

                {error && (
                  <div style={{
                    background: UI.cautionSoft,
                    border: `1px solid ${UI.cautionLine}`,
                    borderRadius: 6, padding: '9px 12px',
                    fontSize: 14, color: UI.caution, fontWeight: 600,
                  }}>
                    {error}
                  </div>
                )}

                <button type="submit" disabled={busy} style={{
                  width: '100%', padding: '12px 0',
                  background: busy ? UI.fill.subtle : UI.accent.base,
                  border: 'none', borderRadius: 6,
                  cursor: busy ? 'default' : 'pointer',
                  color: busy ? UI.text.secondary : UI.surface.page,
                  fontWeight: 800, fontSize: 16,
                  fontFamily: FONT.body,
                  marginTop: 4,
                }}>
                  {busy ? '...' : 'שמור סיסמה חדשה'}
                </button>
              </form>
            </>
          )}
        </div>

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: UI.text.secondary, lineHeight: 1.6 }}>
          <span dir="ltr" style={{ unicodeBidi: 'isolate' }}>Suricata · {new Date().getFullYear()}</span>
        </div>
      </div>
    </div>
  );
}
